import { Box, Dialog, Icon, Portal, Text, VStack, type BoxProps } from "@chakra-ui/react";
import { useRef, type ComponentProps, type ElementType, type ReactNode } from "react";
import { X } from "lucide-react";
import { dialogBackdropMotionProps, dialogContentMotionProps } from "./dialogMotion";

type DialogCommonTone = "primary" | "warning" | "danger" | "neutral";

type DialogContentProps = ComponentProps<typeof Dialog.Content>;
type DialogBodyProps = ComponentProps<typeof Dialog.Body>;
type DialogPositionerProps = ComponentProps<typeof Dialog.Positioner>;

type ToneStyle = {
  surfaceBg: string;
  bodyBg: string;
  headerBg: string;
  headerBorder: string;
  iconColor: string;
  iconShadow: string;
  decorativeIconColor: string;
  eyebrowColor: string;
};

/**
 * Scroll without drawing a bar. A dialog on a short viewport still scrolls by wheel,
 * trackpad, touch and keyboard — it just doesn't put a track down the side of the panel,
 * which reads as a broken layout rather than as scrollable content.
 *
 * Applied to both scroll containers: the positioner (which scrolls a dialog taller than the
 * viewport) and the content (which scrolls the body inside maxH).
 */
const hiddenScrollbarCss = {
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
} as const;

const toneStyles: Record<DialogCommonTone, ToneStyle> = {
  primary: {
    surfaceBg: "#F7FCF9",
    bodyBg: "linear-gradient(180deg, #F7FCF9 0%, rgba(44,185,106,0.04) 100%)",
    headerBg: "linear-gradient(135deg, #2cb96a 0%, #166f3c 100%)",
    headerBorder: "rgba(44,185,106,0.14)",
    iconColor: "#166f3c",
    iconShadow: "0 10px 26px rgba(22,111,60,0.24)",
    decorativeIconColor: "rgba(255,255,255,0.13)",
    eyebrowColor: "rgba(255,255,255,0.72)",
  },
  warning: {
    surfaceBg: "#FFFBF5",
    bodyBg: "linear-gradient(180deg, #FFFBF5 0%, rgba(245,158,11,0.03) 72%, rgba(245,158,11,0.06) 100%)",
    headerBg: "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)",
    headerBorder: "rgba(245,158,11,0.12)",
    iconColor: "#D97706",
    iconShadow: "0 10px 26px rgba(120,53,0,0.2)",
    decorativeIconColor: "rgba(120,53,0,0.18)",
    eyebrowColor: "rgba(255,255,255,0.68)",
  },
  danger: {
    surfaceBg: "#FFF7F7",
    bodyBg: "linear-gradient(180deg, #FFF7F7 0%, rgba(239,68,68,0.04) 100%)",
    headerBg: "linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)",
    headerBorder: "rgba(239,68,68,0.12)",
    iconColor: "#DC2626",
    iconShadow: "0 10px 26px rgba(153,27,27,0.2)",
    decorativeIconColor: "rgba(127,29,29,0.16)",
    eyebrowColor: "rgba(255,255,255,0.72)",
  },
  neutral: {
    surfaceBg: "#F8FAFC",
    bodyBg: "linear-gradient(180deg, #F8FAFC 0%, rgba(15,23,42,0.03) 100%)",
    headerBg: "linear-gradient(135deg, #475569 0%, #0F172A 100%)",
    headerBorder: "rgba(15,23,42,0.12)",
    iconColor: "#334155",
    iconShadow: "0 10px 26px rgba(15,23,42,0.18)",
    decorativeIconColor: "rgba(255,255,255,0.12)",
    eyebrowColor: "rgba(255,255,255,0.7)",
  },
};

export interface DialogCommonProps {
  open: boolean;
  onClose: () => void;
  closeDisabled?: boolean;
  closeOnInteractOutside?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  hideHeader?: boolean;
  title: ReactNode;
  eyebrow?: ReactNode;
  icon?: ElementType;
  tone?: DialogCommonTone;
  children: ReactNode;
  actions?: ReactNode;
  maxW?: DialogContentProps["maxW"];
  contentProps?: Partial<DialogContentProps>;
  bodyProps?: Partial<DialogBodyProps>;
  positionerProps?: Partial<DialogPositionerProps>;
  /** Optional first focus target for dialogs with a primary input, such as an OTP challenge. */
  initialFocusEl?: () => HTMLElement | null;
}

export default function DialogCommon({
  open,
  onClose,
  closeDisabled = false,
  closeOnInteractOutside,
  closeOnEscape,
  showCloseButton = false,
  hideHeader = false,
  title,
  eyebrow,
  icon,
  tone = "primary",
  children,
  actions,
  maxW = { base: "25rem", tablet: "27rem" },
  contentProps,
  bodyProps,
  positionerProps,
  initialFocusEl,
}: DialogCommonProps) {
  const style = toneStyles[tone];
  // Initial focus target. Without this the dialog focuses its first tabbable child,
  // which is the close button — so every dialog opened with a visible ring around the
  // X, reading as though the user had already selected "dismiss". Focus still moves
  // into the dialog (Esc, the focus trap and screen-reader announcement all keep
  // working), it just lands on the container instead of an actionable control.
  const contentRef = useRef<HTMLDivElement>(null);
  const { onPointerDown: positionerOnPointerDown, ...restPositionerProps } =
    positionerProps ?? {};
  // The caller's maxW is honoured on every breakpoint. The base breakpoint used to be
  // forced to 100vw and paired with a 100svh full-bleed sheet, which read as a page the
  // user had navigated to rather than a dialog. Dialogs are cards at every width now,
  // with the positioner's padding providing the gap around them.
  const responsiveMaxW = maxW;

  return (
    <Dialog.Root
      open={open}
      initialFocusEl={initialFocusEl ?? (() => contentRef.current)}
      closeOnInteractOutside={closeOnInteractOutside}
      closeOnEscape={closeOnEscape}
      onOpenChange={(e) => {
        if (!e.open && !closeDisabled) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop
          {...dialogBackdropMotionProps}
          bg="rgba(15,23,42,0.5)"
          backdropFilter="blur(4px)"
          css={{ WebkitBackdropFilter: "blur(4px)" }}
          zIndex={1400}
        />

        <Dialog.Positioner
          position="fixed"
          inset="0"
          display="flex"
          // flex-start, never center: the content carries `my="auto"`, which centres it
          // when it fits and pins it to the top when it does not. Centring here instead
          // would overflow a too-tall dialog equally in both directions, putting its top
          // above the scroll origin where it can never be reached.
          alignItems="flex-start"
          justifyContent="center"
          px={{ base: "1rem", tablet: "0" }}
          py={{ base: "1rem", tablet: "1.5rem" }}
          overflowY="auto"
          zIndex={1400}
          css={hiddenScrollbarCss}
          {...restPositionerProps}
          onPointerDown={(event) => {
            positionerOnPointerDown?.(event);
            if (
              closeOnInteractOutside &&
              !closeDisabled &&
              event.target === event.currentTarget
            ) {
              onClose();
            }
          }}
        >
          <Dialog.Content
            {...dialogContentMotionProps}
            ref={contentRef}
            // -1 so the container can receive programmatic focus without joining the tab
            // order; outline none so receiving it draws nothing.
            tabIndex={-1}
            outline="none"
            w="full"
            maxW={responsiveMaxW}
            minH="auto"
            maxH={{ base: "calc(100dvh - 2rem)", tablet: "calc(100dvh - 3rem)" }}
            mx="0"
            my="auto"
            rounded="1.35rem"
            overflowX="hidden"
            overflowY="auto"
            overscrollBehavior="contain"
            border="1px solid rgba(255,255,255,0.5)"
            boxShadow="0 24px 60px rgba(15,23,42,0.22)"
            bg={style.surfaceBg}
            p={0}
            css={hiddenScrollbarCss}
            {...contentProps}
          >
            {!hideHeader && (
              <Box
                position="relative"
                overflow="hidden"
                // Dialog.Content is a flex column, so this header is a flex item and would
                // otherwise shrink once the dialog hits maxH on a short viewport. Combined
                // with overflow:hidden that clips the icon and title rather than scrolling —
                // the top of the dialog just disappears. Keep its natural height and let
                // Content's overflowY handle the excess.
                flexShrink={0}
                bg={style.headerBg}
                px={{ base: "1.45rem", tablet: "1.75rem" }}
                pt={{ base: "1.5rem", tablet: "1.65rem" }}
                pb={{ base: "1.55rem", tablet: "1.7rem" }}
                textAlign="center"
              >
                {showCloseButton && (
                  <Box
                    as="button"
                    {...({ type: "button", disabled: closeDisabled } as BoxProps)}
                    aria-label="Close dialog"
                    position="absolute"
                    top="0.75rem"
                    right="0.75rem"
                    zIndex={3}
                    w="2.25rem"
                    h="2.25rem"
                    rounded="full"
                    color="white"
                    bg="rgba(255,255,255,0.16)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor={closeDisabled ? "not-allowed" : "pointer"}
                    border="0"
                    _disabled={{ cursor: "not-allowed", opacity: 0.5 }}
                    _hover={{ bg: "rgba(255,255,255,0.26)" }}
                    onClick={() => {
                      if (!closeDisabled) onClose();
                    }}
                  >
                    <X size={15} />
                  </Box>
                )}

                {icon && (
                  <Icon
                    as={icon}
                    position="absolute"
                    right="-1rem"
                    bottom="-1.1rem"
                    boxSize="7rem"
                    color={style.decorativeIconColor}
                    pointerEvents="none"
                  />
                )}

                <VStack align="center" gap="0.55rem" position="relative" zIndex={1}>
                  {icon && (
                    <Box
                      w="3.15rem"
                      h="3.15rem"
                      rounded="full"
                      bg="white"
                      border="1px solid rgba(255,255,255,0.28)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow={style.iconShadow}
                    >
                      <Icon as={icon} boxSize="1.38rem" color={style.iconColor} strokeWidth={2.4} />
                    </Box>
                  )}

                  {eyebrow && (
                    <Text
                      fontSize="0.68rem"
                      fontWeight="900"
                      letterSpacing="0.12em"
                      textTransform="uppercase"
                      color={style.eyebrowColor}
                    >
                      {eyebrow}
                    </Text>
                  )}

                  <Dialog.Title
                    fontSize={{ base: "1.25rem", tablet: "1.35rem" }}
                    fontWeight="900"
                    color="white"
                    lineHeight="1.15"
                  >
                    {title}
                  </Dialog.Title>
                </VStack>
              </Box>
            )}

            <Dialog.Body
              p="0"
              bg={style.bodyBg}
              borderTop="1px solid"
              borderColor={style.headerBorder}
              overflow="visible"
              {...bodyProps}
            >
              {children}
              {actions && (
                <Box px={{ base: "1.45rem", tablet: "1.75rem" }} pb="1.35rem" pt="0.2rem">
                  {actions}
                </Box>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
